import React from "react"
import { Markdown, MarkdownProps, defaultMarkdownElements } from "#/services/markdown"
import { Typography } from "@carrotfertility/carotene-core"
import {
  LinkModalContextProvider,
  LinkToModal,
  ModalFromLink,
  ArrowLink,
  CalloutBox,
  PhoneSupportNumberLink,
  PhoneSupportNumberText,
  SelectedTalkToCarrotLocation,
  CarrotMatchLink,
  SDoHFindHelpLink,
  SDoHQuestionLink,
  PrimaryButton,
  SecondaryButton
} from "./custom-components"

import { PiiText } from "#/services/tracking"
import { useSelector } from "react-redux"
import { getCompanyDisplayName } from "#/redux/reducers/companyInfo"
import useBenefit from "#/components/dynamic-content/hooks/useGetBenefit"
import { validateContentSource } from "./util/content-validation"
import { PayingForCareCoverageDetails } from "#/components/views/reimbursements/digital-benefit-guide/PayingForCare"

import { preprocess } from "../markdown/preprocessors"
import { useResolveText } from "./hooks/useResolveText"
import { TelemedicineForMenopauseLink } from "#/services/contentful/custom-components/telemedicine-for-mopause-link"
import { CarrotMatchContextProvider } from "#/components/carrot-match-fertility-clinic-precision-matching/context/CarrotMatchContext"
import { RecommendDoulaLink } from "#/services/contentful/custom-components/recommend-doula-link"
import {
  TelemedicineForPediatricsButton,
  TelemedicineForPediatricsLink
} from "#/services/contentful/custom-components/telemedicine-for-pediatrics-link"
import {
  TelemedicineForPelvicFloorTherapyButton,
  TelemedicineForPelvicFloorTherapyLink
} from "#/services/contentful/custom-components/telemedicine-for-pelvic-floor-therapy-link"

export type ContentfulMarkdownOverrides = Omit<MarkdownProps, "children">

export const ContentfulMarkdownOverrideContext = React.createContext<ContentfulMarkdownOverrides>({})

export function ContentfulRawMarkdownRenderer({ children, ...markdownProps }: MarkdownProps) {
  return (
    <ContentfulMarkdownOverrideContext.Provider value={markdownProps || {}}>
      <LinkModalContextProvider>
        <CarrotMatchContextProvider>
          <ContentfulMarkdown>{children}</ContentfulMarkdown>
        </CarrotMatchContextProvider>
      </LinkModalContextProvider>
    </ContentfulMarkdownOverrideContext.Provider>
  )
}

export function ContentfulMarkdown(props: Pick<MarkdownProps, "children">) {
  const { overrides, ...otherMarkdownOverrides } = React.useContext(ContentfulMarkdownOverrideContext)

  const customMarkdownElements = {
    LinkToModal,
    ModalFromLink,
    ArrowLink,
    PrimaryButton,
    SecondaryButton,
    CoverageAmountsDbg: PayingForCareCoverageDetails,
    CalloutBox,
    BodySmall: {
      ...defaultMarkdownElements.p,
      props: {
        ...defaultMarkdownElements.p?.props,
        variant: "body2",
        sx: {
          "> p": {
            fontSize: "inherit"
          }
        }
      }
    },
    CompanyDisplayName: ({ children }: { children: React.ReactNode }) => {
      const companyDisplayNameInfo = useSelector(getCompanyDisplayName)
      if (children) {
        return (
          <Typography
            component="p"
            paddingBlockEnd={(theme) => theme.spacing(theme.tokens.spacing.md)}
            sx={{
              ":last-child": {
                paddingBlockEnd: 0
              }
            }}
          >
            <PiiText>{companyDisplayNameInfo}</PiiText>
            {children}
          </Typography>
        )
      }
      return <PiiText>{companyDisplayNameInfo}</PiiText>
    },
    CustomCoverage: () => {
      const {
        data: {
          customizations: { memberCustomCoverageExplanationMarkdown }
        }
      } = useBenefit()

      return memberCustomCoverageExplanationMarkdown && <Markdown>{memberCustomCoverageExplanationMarkdown}</Markdown>
    },
    AdditionalEmployerBenefits: () => {
      const {
        data: {
          customizations: { additionalEmployerBenefitMarkdown }
        }
      } = useBenefit()

      return additionalEmployerBenefitMarkdown && <Markdown>{additionalEmployerBenefitMarkdown}</Markdown>
    },
    HealthPlan: () => {
      const {
        data: {
          customizations: { healthPlanMarkdown }
        }
      } = useBenefit()

      return healthPlanMarkdown && <Markdown>{healthPlanMarkdown}</Markdown>
    },
    PhoneSupportNumberLink,
    PhoneSupportNumberText,
    SelectedTalkToCarrotLocation,
    CarrotMatchLink,
    SDoHFindHelpLink,
    SDoHQuestionLink,
    TelemedicineForMenopauseLink,
    RecommendDoulaLink,
    TelemedicineForPediatricsLink,
    TelemedicineForPediatricsButton,
    TelemedicineForPelvicFloorTherapyLink,
    TelemedicineForPelvicFloorTherapyButton,
    ...overrides
  }

  const source = validateContentSource(props.children, customMarkdownElements)
  const resolvedSource = useResolveText(source)

  return (
    <Markdown overrides={customMarkdownElements} {...otherMarkdownOverrides} {...props}>
      {preprocess(resolvedSource)}
    </Markdown>
  )
}
